import { useMemo } from "react";

import { useRequest } from "~/v2/api/hooks";
import {
  getAchTransfers,
  getRapydTransfers,
  type Transfer,
} from "~/v2/api/rest/banking";
import { getCurrencyCloudTransfers } from "~/v2/api/rest/currencyCloud";
import { queryKeys } from "../queryKeys";

type UseGetAllTransfersProps = {
  accountID: string;
  enabled?: boolean;
  isCurrencyCloudEnabled?: boolean;
  refetchOnMount?: boolean | "always";
};

export type UseGetAllTransfersReturn = {
  achAndWire: {
    transfers: Transfer[];
    isLoading: boolean;
    refetch: () => void;
  };
  rapyd: {
    transfers: Transfer[];
    isLoading: boolean;
    refetch: () => void;
  };
  currencyCloud: {
    transfers: Transfer[];
    isLoading: boolean;
    refetch: () => void;
  };
};

export const useGetAllTransfers = ({
  accountID,
  enabled = true,
  isCurrencyCloudEnabled,
  refetchOnMount,
}: UseGetAllTransfersProps): UseGetAllTransfersReturn => {
  // This actually includes wire transfers too
  const achAndWire = useRequest(
    queryKeys.funding.achTransfers(accountID),
    () => getAchTransfers(accountID),
    {
      enabled: enabled && !!accountID,
      refetchOnMount,
    },
  );

  const rapyd = useRequest(
    queryKeys.funding.rapydTransfers(accountID),
    () => getRapydTransfers(accountID),
    {
      enabled: enabled && !!accountID,
      refetchOnMount,
    },
  );

  const currencyCloud = useRequest(
    queryKeys.funding.ccTransfers(accountID),
    () => getCurrencyCloudTransfers(accountID),
    {
      enabled: enabled && !!accountID && isCurrencyCloudEnabled,
      refetchOnMount,
    },
  );

  const ccTransfers: Transfer[] = useMemo(
    () =>
      currencyCloud.data?.map((cc) => ({
        id: cc.id,
        status: cc.status,
        amount: parseFloat(cc.amount),
        created_at: cc.created_at,
        type: "CurrencyCloud",
        direction: cc.direction,
      })) ?? [],
    [currencyCloud.data],
  );

  return {
    achAndWire: {
      transfers: achAndWire.data || [],
      isLoading: achAndWire.isLoading,
      refetch: achAndWire.refetch,
    },
    rapyd: {
      transfers: rapyd.data || [],
      isLoading: rapyd.isLoading,
      refetch: rapyd.refetch,
    },
    currencyCloud: {
      transfers: ccTransfers || [],
      isLoading: currencyCloud.isLoading,
      refetch: currencyCloud.refetch,
    },
  };
};
